import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Team } from 'Src/models/team';
import { getKitLabKits, KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import {
  Gender,
  KitBottomPattern,
  DetailColorSource,
  SleevePattern,
  KitPatternVariant,
  ShortsSideTrianglesPosition
} from 'Src/types/kitLab';
import './SchoolTeamKitSummary.scss';

interface LayoutPresetObj {
  shirtPattern: KitPatternVariant;
  bottomPattern: KitBottomPattern;

  primaryColor: string;
  secondaryColor: string;
  shortsPrimaryColor: string;
  shortsSecondaryColor: string;

  showPocket: boolean;
  showCollar: boolean;
  showSleeves: boolean;
  showShoulderTriangles: boolean;
  showSleeveCuffs: boolean;

  pocketColorSource: DetailColorSource;
  sleevesColorSource: DetailColorSource;
  collarColorSource: DetailColorSource;
  shoulderTrianglesColorSource: DetailColorSource;
  sleeveCuffsColorSource: DetailColorSource;

  pocketCustomColor: string;
  sleevesCustomColor: string;
  collarCustomColor: string;
  shoulderTrianglesCustomColor: string;
  sleeveCuffsCustomColor: string;

  collarStyle: 'ROUND' | 'POLO';

  stripeCount: number;
  stripesEqualWidth: boolean;
  stripePattern: number[];

  skortStripeCount: number;
  skortStripesEqualWidth: boolean;
  skortStripePattern: number[];

  diagonalDirection: 'LEFT' | 'RIGHT';
  chevronSize: 'SMALL' | 'BIG';
  chevronDirection: 'UP' | 'DOWN';
  chevronPosition: 'TOP' | 'MIDDLE' | 'BOTTOM';
  sideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  skortSideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  shortsTrianglesMode: ShortsSideTrianglesPosition;
  sleevePattern: SleevePattern;
}

const DEFAULT_LAYOUT: LayoutPresetObj = {
  shirtPattern: 'plain',
  bottomPattern: 'SHORTS_PLAIN',

  primaryColor: '#1f78ff',
  secondaryColor: '#ffd24d',
  shortsPrimaryColor: '#1f78ff',
  shortsSecondaryColor: '#ffd24d',

  showPocket: true,
  showCollar: true,
  showSleeves: true,
  showShoulderTriangles: false,
  showSleeveCuffs: false,

  pocketColorSource: 'SECONDARY',
  sleevesColorSource: 'SECONDARY',
  collarColorSource: 'SECONDARY',
  shoulderTrianglesColorSource: 'SECONDARY',
  sleeveCuffsColorSource: 'SECONDARY',

  pocketCustomColor: '#ffffff',
  sleevesCustomColor: '#ffffff',
  collarCustomColor: '#ffffff',
  shoulderTrianglesCustomColor: '#ffffff',
  sleeveCuffsCustomColor: '#ffffff',

  collarStyle: 'ROUND',

  stripeCount: 5,
  stripesEqualWidth: true,
  stripePattern: [1, 1, 1, 1, 1],

  skortStripeCount: 5,
  skortStripesEqualWidth: true,
  skortStripePattern: [1, 1, 1, 1, 1],

  diagonalDirection: 'RIGHT',
  chevronSize: 'SMALL',
  chevronDirection: 'DOWN',
  chevronPosition: 'MIDDLE',
  sideTrianglesMode: 'BOTH',
  skortSideTrianglesMode: 'BOTH',
  shortsTrianglesMode: 'BOTH',
  sleevePattern: 'PLAIN'
};

function isSkortPatternId(pattern?: KitBottomPattern): boolean {
  if (!pattern) return false;
  return (
    pattern === 'SKORT_PLAIN' ||
    pattern === 'SKORT_VERTICAL_STRIPES' ||
    pattern === 'SKORT_SIDE' ||
    pattern === 'SKORT_TRIANGLES' ||
    pattern === 'SKORT_BOTTOM_TRIANGLES' ||
    pattern === 'SKORT_SEMICIRCLES'
  );
}

function mapBottomToSkortPattern(
  pattern: KitBottomPattern | undefined
): 'plain' | 'verticalStripes' | 'sideStripes' | 'sideTriangles' | 'sideBottomTriangles' | 'sideSemiCircles' {
  switch (pattern) {
    case 'SKORT_VERTICAL_STRIPES':
      return 'verticalStripes';
    case 'SKORT_SIDE':
      return 'sideStripes';
    case 'SKORT_TRIANGLES':
      return 'sideTriangles';
    case 'SKORT_BOTTOM_TRIANGLES':
      return 'sideBottomTriangles';
    case 'SKORT_SEMICIRCLES':
      return 'sideSemiCircles';
    case 'SKORT_PLAIN':
    default:
      return 'plain';
  }
}

function resolveDetailColor(source: DetailColorSource, custom: string, primary: string, secondary: string): string {
  if (source === 'PRIMARY') return primary;
  if (source === 'SECONDARY') return secondary;
  return custom || secondary || primary;
}

function getLayoutForKit(kit: KitLabBackendKit): LayoutPresetObj {
  let base: LayoutPresetObj = { ...DEFAULT_LAYOUT };

  if (kit.layoutPreset) {
    try {
      const parsed = JSON.parse(kit.layoutPreset);
      if (parsed && typeof parsed === 'object') {
        base = { ...base, ...(parsed as Partial<LayoutPresetObj>) };
      }
    } catch (e) {}
  } else if (Array.isArray(kit.segments)) {
    const primarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_PRIMARY');
    const secondarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_SECONDARY');
    const shortsPrimarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_PRIMARY');
    const shortsSecondarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_SECONDARY');

    base.primaryColor = primarySeg?.color || base.primaryColor;
    base.secondaryColor = secondarySeg?.color || base.secondaryColor;
    base.shortsPrimaryColor = shortsPrimarySeg?.color || base.shortsPrimaryColor;
    base.shortsSecondaryColor = shortsSecondarySeg?.color || base.shortsSecondaryColor;
  }

  return base;
}

interface Props {
  user: AppUser;
  team: Team;
  onChooseKit: () => void;
  onClearKit: () => void;
}

interface State {
  isLoadingKit: boolean;
  kit: KitLabBackendKit | null;
}

export class SchoolTeamKitSummary extends React.Component<Props, State> {
  state: State = {
    isLoadingKit: false,
    kit: null
  };

  componentDidMount() {
    this.loadKit();
  }

  componentDidUpdate(prevProps: Props) {
    const prevKitId = propz.get(prevProps.team as any, ['kitLabKitId'], null);
    const nextKitId = propz.get(this.props.team as any, ['kitLabKitId'], null);

    if (prevKitId !== nextKitId || prevProps.user !== this.props.user) {
      this.loadKit();
    }
  }

  loadKit() {
    const { user, team } = this.props;
    const kitId = propz.get(team as any, ['kitLabKitId'], null);

    if (!kitId) {
      this.setState({ kit: null, isLoadingKit: false });
      return;
    }

    this.setState({ isLoadingKit: true });

    getKitLabKits(user)
      .then(kits => {
        const all = kits || [];
        const found = all.find(k => k._id && k._id.toString() === kitId.toString());
        this.setState({
          kit: found || null,
          isLoadingKit: false
        });
      })
      .catch(() => {
        this.setState({
          kit: null,
          isLoadingKit: false
        });
      });
  }

  renderEmpty() {
    const { onChooseKit } = this.props;

    return (
      <div className="eTeamKitSummary__empty">
        <span className="eTeamKitSummary__emptyIcon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M8.5 4 5 5.5 2.8 9.8l3 1.5L7 9v11h10V9l1.2 2.3 3-1.5L19 5.5 15.5 4A4.7 4.7 0 0 1 12 5.5 4.7 4.7 0 0 1 8.5 4Z" />
          </svg>
        </span>
        <strong>No team kit selected</strong>
        <p>Choose a kit to make this team easy to recognise.</p>
        <button type="button" className="eTeamKitSummary__primary" onClick={onChooseKit}>
          Choose kit
        </button>
      </div>
    );
  }

  renderContent() {
    const { kit } = this.state;
    const { onChooseKit, onClearKit } = this.props;

    if (!kit) {
      return this.renderEmpty();
    }

    const layout = getLayoutForKit(kit);

    const kitGenderRaw = (kit.gender || '').toString().toUpperCase() as Gender | '' | undefined;

    const genderText = kitGenderRaw === 'MALE' ? 'Boys' : kitGenderRaw === 'FEMALE' ? 'Girls' : 'Any team';

    const hasSports = Array.isArray(kit.sportIds) && kit.sportIds.length > 0;
    const sportsText = hasSports ? 'Sport-specific' : 'All sports';

    const pocketColor = resolveDetailColor(
      layout.pocketColorSource,
      layout.pocketCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleevesColor = resolveDetailColor(
      layout.sleevesColorSource,
      layout.sleevesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const collarColor = resolveDetailColor(
      layout.collarColorSource,
      layout.collarCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const shoulderTrianglesColor = resolveDetailColor(
      layout.shoulderTrianglesColorSource,
      layout.shoulderTrianglesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleeveCuffsColor = resolveDetailColor(
      layout.sleeveCuffsColorSource,
      layout.sleeveCuffsCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );

    const isSkort = isSkortPatternId(layout.bottomPattern);

    return (
      <div className="eTeamKitSummary__content">
        <div className="eTeamKitSummary__preview">
          <KitShirtPreview
            variant={layout.shirtPattern}
            primaryColor={layout.primaryColor}
            secondaryColor={layout.secondaryColor}
            showPocket={layout.showPocket}
            showCollar={layout.showCollar}
            showSleeves={layout.showSleeves}
            showShoulderTriangles={layout.showShoulderTriangles}
            showSleeveCuffs={layout.showSleeveCuffs}
            pocketColor={pocketColor}
            sleevesColor={sleevesColor}
            collarColor={collarColor}
            shoulderTrianglesColor={shoulderTrianglesColor}
            sleeveCuffsColor={sleeveCuffsColor}
            stripeCount={layout.stripeCount}
            stripesEqualWidth={layout.stripesEqualWidth}
            stripePattern={layout.stripePattern}
            diagonalDirection={layout.diagonalDirection}
            chevronSize={layout.chevronSize}
            chevronDirection={layout.chevronDirection}
            chevronPosition={layout.chevronPosition}
            sideTrianglesMode={layout.sideTrianglesMode}
            sleevePattern={layout.sleevePattern}
            collarStyle={layout.collarStyle}
            className="eKitLab__shirtSvg--small"
          />

          {isSkort ? (
            <KitSkortPreview
              pattern={mapBottomToSkortPattern(layout.bottomPattern)}
              primaryColor={layout.shortsPrimaryColor}
              secondaryColor={layout.shortsSecondaryColor}
              stripeCount={layout.skortStripeCount}
              stripesEqualWidth={layout.skortStripesEqualWidth}
              stripePattern={layout.skortStripePattern}
              sideTrianglesMode={layout.skortSideTrianglesMode}
              className="eKitLab__bottomSvg--small eKitLab__bottomSvg--smallSkort"
            />
          ) : (
            <KitShortsPreview
              variant={
                layout.bottomPattern === 'SHORTS_SIDE'
                  ? 'sideStripes'
                  : layout.bottomPattern === 'SHORTS_TRIANGLES'
                  ? 'sideTriangles'
                  : layout.bottomPattern === 'SHORTS_BIG_TRIANGLES'
                  ? 'sideBigTriangles'
                  : layout.bottomPattern === 'SHORTS_SEMICIRCLES'
                  ? 'sideSemiCircles'
                  : 'plain'
              }
              primaryColor={layout.shortsPrimaryColor}
              secondaryColor={layout.shortsSecondaryColor}
              sideTrianglesPosition={layout.shortsTrianglesMode}
              className="eKitLab__bottomSvg--small"
            />
          )}
        </div>

        <div className="eTeamKitSummary__info">
          <span className="eTeamKitSummary__eyebrow">Team kit</span>
          <div className="eTeamKitSummary__kitName">{kit.name || 'Unnamed kit'}</div>
          <div className="eTeamKitSummary__kitMeta">
            {genderText} · {sportsText}
          </div>
          <div className="eTeamKitSummary__buttons">
            <button type="button" className="eTeamKitSummary__primary" onClick={onChooseKit}>
              Change kit
            </button>
            <button type="button" className="eTeamKitSummary__removeBtn" onClick={onClearKit}>
              Remove kit
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isLoadingKit } = this.state;

    return (
      <div className="eTeamKitSummary">
        {isLoadingKit ? <div className="eTeamKitSummary__empty">Loading kit…</div> : this.renderContent()}
      </div>
    );
  }
}

export default SchoolTeamKitSummary;
